
import {
  Vue, Component, Prop, Inject,
} from 'vue-property-decorator';
import {
  AvailableService,
  CarrierLabelResult,
  InProgress,
  ShipFrom,
  ShipTo,
  ThreeDsCardLookupDto,
} from '@/features/shipment/types';
import {
  checkCardVerificationRequired,
  checkToShowSummaryPage,
  syncInProgressData,
} from '@/features/shipment/services/shipment.services';
import { createLabel, getBrowserMetadata, getShipToAddressInPlainText } from '@/features/shipment/helpers/createShipment';
import { trackShipmentCompleted } from '@/features/common/helpers/tracked-events';
import { getResultsNotifications } from '@/features/notifications/helpers/results';
import { EstimateDataType } from '@/features/estimate/types';
import { defaultInProgressData, freightServices } from '@/features/common/raw';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { NotificationData, UserPreferences } from '@/features/settings/types';
import { showWarningAlertDialog, getCheapestService, getFastestService } from '@/features/common/helpers';
import { isAlphaNumeric, isNumeric } from '@/features/common/helpers/strUtils';
import DataTableWrapper from '@/features/common/components/DataTableWrapper.vue';
import ServiceItem from '@/features/shipment/components/results/ServiceItem.vue';
import HighlightedService from '@/features/shipment/components/results/HighlightedService.vue';
import { handle3dsCardLookup, redirectTo3dsChallenge, CardLookupError } from '@/features/shipment/helpers/threeDsHelper';
import FreightOptionsModal from './FreightOptionsModal.vue';
import DeliveryAppointmentModal from './DeliveryAppointmentModal.vue';
import AdditionalReturnOptionsModal from './AdditionalReturnOptionsModal.vue';

@Component({
  components: {
    DataTableWrapper,
    ServiceItem,
    FreightOptionsModal,
    DeliveryAppointmentModal,
    AdditionalReturnOptionsModal,
    HighlightedService,
  },
})
export default class ResultsTable extends Vue {
  @Inject({ default: false }) readonly isEstimatePage!: boolean;

  @Prop({ required: true }) services!: Array<AvailableService>

  @Prop({ required: true }) priceSortingActive!: boolean;

  @Prop({ required: true }) dateSortingActive!: boolean;

  @Prop({ required: true }) order!: boolean;

  @Prop({ default: false }) isReceiversAcc!: boolean;

  get hasFedExServices(): boolean {
    return this.services.some((s) => s.carrierCode === 'FedEx');
  }

  private syncedShow = false;

  private isShowSummaryPage = false;

  private labelResponse: CarrierLabelResult | null = null;

  private isShowFreightOptions = false;

  private isShowAppointmentModal = false;

  private isFreightFormChanged = false;

  private selectedService: AvailableService | null = null;

  get inProgressData(): InProgress {
    return this.$store.getters.getInProgress;
  }

  get isInProgressData(): boolean {
    return this.$store.getters.getIsInProgressData;
  }

  get userPreferences(): UserPreferences {
    return this.$store.getters.getUserPreferences;
  }

  get selectedServiceLevel(): string | undefined {
    return this.$route.query.serviceLevel as string;
  }

  get cheapestServiceItem(): AvailableService | undefined {
    return getCheapestService(this.services);
  }

  get fastestServiceItem(): AvailableService | undefined {
    return getFastestService(this.services);
  }

  mounted(): void {
    if (!this.isEstimatePage) {
      this.checkSummaryPageToShow();
    }
  }

  private async checkSummaryPageToShow() {
    this.isShowSummaryPage = await checkToShowSummaryPage();
    this.$store.commit('setShowSummaryPage', this.isShowSummaryPage);
  }

  get isInternationalShipment(): boolean {
    const { fromAddress, toAddress } = this.inProgressData;
    return fromAddress?.countryCode !== toAddress?.countryCode;
  }

  private onServiceExpand(service: AvailableService): void {
    if (this.isEstimatePage) return;
    this.$store.dispatch('fetchLandedCost', {
      service,
      isInternational: this.isInternationalShipment,
    });
  }

  private async select(item: AvailableService): Promise<void> {
    this.selectedService = item;

    if (this.isEstimatePage) {
      this.loadAndSaveEstimateDataToShipment();
      return;
    }

    if (this.isReceiversAcc) {
      eventBus.$emit(eventNames.VALIDATE_RECEIVERS_ACCOUNT_DETAILS);

      if (!this.isValidReceiversAccountNumber(item.carrierCode)) {
        eventBus.$emit(eventNames.OPEN_ESTIMATE_RESULTS_DETAIL);
        return;
      }
    }

    if (this.showFreightOptions()) return;

    if (this.showAppointmentModalIfRequired()) return;

    this.getResults();
  }

  private getResults(isPriceChanged = false) {
    if (!this.selectedService) return;

    const onNotificationsSubmit = async (): Promise<void> => {
      if (!this.selectedService) return;

      this.$store.commit('setLoading', true);
      this.$store.commit('setSelectedService', this.selectedService);
      await this.saveInProgressData(this.selectedService);

      if (this.isShowSummaryPage || isPriceChanged) {
        this.$router.push({ name: 'shipment-checkout' });
      } else {
        const isVerificationRequired = await checkCardVerificationRequired();
        if (isVerificationRequired) {
          this.$router.push({ name: 'shipment-verify' });
          return;
        }

        let threeDsData: ThreeDsCardLookupDto | null = null;
        try {
          threeDsData = await handle3dsCardLookup();
        } catch (error) {
          if (error instanceof CardLookupError) {
            this.labelResponse = {
              successful: false,
              errorMessage: error.errorMessage,
              redirectUrl: '',
              isCarrierMessage: false,
            } as CarrierLabelResult;
            this.syncedShow = true;
            this.$store.commit('setLoading', false);
            return;
          }
          // If it's not a CardLookupError, rethrow it
          throw error;
        }

        const metaData = getBrowserMetadata(threeDsData?.cardLookupId);

        this.labelResponse = await createLabel(true, metaData);
        if (this.labelResponse && this.labelResponse.successful) {
          this.$router.push({
            name: 'shipment-print',
            query: { id: this.labelResponse.secureshipTrackingNo },
          });
          this.$store.dispatch('clearInProgressDataInShortly');
          this.$store.dispatch('clearEstimateDataInShortly');
          this.$store.dispatch('refetchUserInfo');
          const selectedSvc = this.$store.getters.getSelectedService;
          trackShipmentCompleted(this.$gtag, {
            shipmentId: this.labelResponse.secureshipTrackingNo,
            value: selectedSvc?.total || 0,
          });
        } else if (this.labelResponse?.errorType === 'CreditCardChallengeRequired' && this.labelResponse?.threeDsChallenge) {
          await redirectTo3dsChallenge(this.labelResponse.threeDsChallenge);
          return;
        } else {
          this.syncedShow = true;
        }
      }

      this.$store.commit('setLoading', false);
    };

    getResultsNotifications(this.selectedService, onNotificationsSubmit);
  }

  private async saveInProgressData(service: AvailableService) {
    const inProgressData: InProgress = {
      ...this.inProgressData,
      serviceLevel: service.selectedService,
      useSecureship: service.useSecureship,
    };

    if (this.isFreightService(service.selectedService)) {
      inProgressData.packages = inProgressData.packages.map(
        (p) => ({ ...p, packageType: 'Pallet' }),
      );
    }

    this.$store.commit('setInProgress', inProgressData);
    await syncInProgressData(this.inProgressData);
  }

  private async loadAndSaveEstimateDataToShipment() {
    const payload: EstimateDataType = this.$store.state.estimate.estimateData;
    const { defaultAddress } = this.userPreferences.shippingSettings;

    const fromAddressIsHome = payload.fromAddress.postalCode === defaultAddress?.postalCode
      && payload.fromAddress.countryCode === defaultAddress.countryCode;

    const toAddressIsHome = payload.toAddress.postalCode === defaultAddress?.postalCode
      && payload.toAddress.countryCode === defaultAddress.countryCode;

    const fromAddress = fromAddressIsHome ? {
      ...defaultAddress,
      isInside: payload.fromAddress.isInside,
      isLimitedAccess: payload.fromAddress.isLimitedAccess,
      isTailGate: payload.fromAddress.isTailGate,
      isTradeShow: payload.fromAddress.isTradeShow,
    } : payload.fromAddress;

    const toAddress = toAddressIsHome ? {
      ...defaultAddress,
      isInside: payload.toAddress.isInside,
      isLimitedAccess: payload.toAddress.isLimitedAccess,
      isTailGate: payload.toAddress.isTailGate,
      isTradeShow: payload.toAddress.isTradeShow,
    } : payload.toAddress;

    const isToAddressInProgress = Object.values(this.inProgressData.toAddress)
      .some((v) => v && v.length);

    const saveAndRedirect = async () => {
      this.$store.commit('setInProgress', {
        ...defaultInProgressData,
        fromAddress,
        toAddress,
        packages: payload.packages,
        documentsOnly: payload.isDocumentsOnly,
        signatureOptions: payload.signatureOptions,
      });

      await this.$store.dispatch('syncInProgressData');

      this.$router.push({ name: 'shipment-create' });
    };

    if (isToAddressInProgress && !toAddressIsHome) {
      eventBus.$once(eventNames.CONFIRMATION_DIALOG, saveAndRedirect);
      this.$store.commit('toggleConfirmDialog', {
        show: true,
        content: this.$t('shipment.editShipmentPrompt', {
          shipTo: getShipToAddressInPlainText(this.inProgressData.toAddress),
        }) as string,
        btnText: this.$t('buttons.ok') as string,
        title: this.$t('alertDialog.currentlyWorkingOn') as string,
      });

      return;
    }

    await saveAndRedirect();
  }

  private isValidReceiversAccountNumber(carrierCode: string): boolean {
    const account = this.inProgressData.billing.account?.toString().trim() ?? '';
    const accountLength = account.length;

    if (!this.inProgressData.carrierCode || !accountLength) {
      showWarningAlertDialog(this.$t('shipment.alertDialog.enterCarrierInfo') as string);
      return false;
    }

    if (carrierCode === 'UPS' && (accountLength !== 6 || !isAlphaNumeric(account))) {
      showWarningAlertDialog(this.$t('shipment.alertDialog.upsCarrierAccountValidation') as string);
      return false;
    }

    if (carrierCode === 'FedEx' && (accountLength !== 9 || !isNumeric(account))) {
      showWarningAlertDialog(this.$t('shipment.alertDialog.fedexCarrierAccountValidation') as string);
      return false;
    }

    return true;
  }

  private isFreightOptionsSelected() {
    const isSelected = (address: ShipFrom | ShipTo) => address.isTailGate
      || address.isTradeShow
      || address.isInside
      || address.isLimitedAccess;
    const { fromAddress, toAddress } = this.inProgressData;
    return isSelected(fromAddress) || isSelected(toAddress);
  }

  private showFreightOptions(): boolean {
    if (!this.selectedService) return false;
    const serviceName = this.selectedService.selectedService;

    if (!this.isFreightService(serviceName)) return false;

    const isMultiPackageWithMyPackage = () => {
      const { packages } = this.inProgressData;
      if (packages.length < 2) return false;
      if (!packages.some((p) => p.packageType === 'MyPackage')) return false;
      return true;
    };

    if (!isMultiPackageWithMyPackage()) {
      if (this.isFreightOptionsSelected()) return false;
      this.isShowFreightOptions = true;
    } else {
      this.showMultiPackagesFreightWarning();
    }

    return true;
  }

  private showAppointmentModalIfRequired(): boolean {
    if (!this.selectedService) return false;
    if (!this.isFreightService(this.selectedService.selectedService)) return false;

    const { toAddress } = this.inProgressData;
    if (!toAddress.appointment) return false;

    const { appointment } = toAddress;
    if (appointment.appointmentType === 'Scheduled') {
      this.isShowAppointmentModal = true;
      return true;
    }

    return false;
  }

  private goNextStepForFreight() {
    if (!this.selectedService) return;
    if (this.isFreightOptionsSelected()) return;
    if (this.showAppointmentModalIfRequired()) return;
    this.getResults();
  }

  private showMultiPackagesFreightWarning() {
    const notificationData: NotificationData = {
      name: 'FreightMultiPackagesWarning',
      showCount: false,
      showDontShowCheck: false,
    };

    this.$store.dispatch('showNotification', [notificationData]);

    eventBus.$once(eventNames.NOTIFICATION_SUBMIT, () => {
      this.$router.push({ name: 'shipment-create' });
    });
  }

  private async updateRatesAndGetResults(isPriceChanged: boolean) {
    this.$store.dispatch('getRates', { showLoading: false });
    this.getResults(isPriceChanged);
  }

  private isFreightService = (serviceName: string) => {
    return freightServices.includes(serviceName);
  }
}
